import { useEffect, useMemo, useState } from 'react';
import { Box, SelectChangeEvent } from '@mui/material';
import { useSelector } from 'react-redux';
import PageHeader from '../../../../components/PageHeader';
import { sharpsIcon } from '../../../../icons';
import ComplianceStatus from '../../../../components/ComplianceStatus';
import Stats from '../../../../components/Stats';
import { selectChildPractices, selectCurrentCustomerNumber, selectGroups, selectLoadingState } from '../../../../redux/customer';
import { LoadingState, WasteManagementComplianceCounts} from '../../../../types';
import WasteMangementTable from '../components/WasteManagementTable';
import ExportButton from '../components/ExportButton';

const initComplianceCounts: WasteManagementComplianceCounts = {
  actionRequired: 0,
  attentionNeeded: 0,
  upToDate: 0,
  inactive: 0,
  inUse: 0,
  processed: 0,
  actionRequiredPercentage: 0,
  attentionNeededPercentage: 0,
  upToDatePercentage: 0,
  inactivePercentage: 0
};

const MedicalWaste = () => {

  const customerNumber = useSelector(selectCurrentCustomerNumber);
  const groups = useSelector(selectGroups);
  const childPractices = useSelector(selectChildPractices);
  const loadingState = useSelector(selectLoadingState)

  const [complianceCounts, setComplianceCounts] = useState(initComplianceCounts);
  const [complianceFilter, setComplianceFilter] = useState<number | undefined>(undefined);

  const isLoading = loadingState === LoadingState.Loading

  const calculatePercentage = (count: number, total: number) => {
    const percentage = (count / total) * 100;

    if (isNaN(percentage)) {
      return 0;
    }
    return Math.round(percentage);
  };

  const handleCheckboxComplianceFilter = (value: number) => {
    setComplianceFilter((prev) => (prev === value ? undefined : value));
  };

  const handleComplianceFilter = (event: SelectChangeEvent<number | undefined>) => {
    const rawValue = event.target.value;
    const parsedValue = Number(rawValue);
    const value = isNaN(parsedValue) || parsedValue === 0 ? undefined : parsedValue;

    setComplianceFilter((prev) => (prev === value ? undefined : value));
  };

  const statsContent = useMemo(() => {
    return [
      {
        description: 'Total in Use',
        value: complianceCounts.inUse,
        color: 'blue'
      },
      {
        description: 'Total Processed',
        value: complianceCounts.processed,
        color: 'blue'
      }
    ];
  }, [complianceCounts]);

  useEffect(() => {
    if (childPractices.length > 0) {
      const complianceCounts = structuredClone(initComplianceCounts);

      childPractices.forEach((practice) => {
        switch (practice.mwaste_status) {
          case 1:
            complianceCounts.upToDate++;
            break;
          case 2:
            complianceCounts.attentionNeeded++;
            break;
          case 3:
            complianceCounts.actionRequired++;
            break;
          case 4:
            complianceCounts.inactive++;
            break;
          default:
            complianceCounts.inactive++;
        }

        complianceCounts.inUse += practice.mwaste_available_containers;
        complianceCounts.processed += practice.mwaste_destroyed_containers;
      });

      const totalPractices = childPractices.length;
      complianceCounts.actionRequiredPercentage = calculatePercentage(complianceCounts.actionRequired, totalPractices);
      complianceCounts.attentionNeededPercentage = calculatePercentage(complianceCounts.attentionNeeded, totalPractices);
      complianceCounts.upToDatePercentage = calculatePercentage(complianceCounts.upToDate, totalPractices);
      complianceCounts.inactivePercentage = calculatePercentage(complianceCounts.inactive, totalPractices);

      setComplianceCounts(complianceCounts);
    }
  }, [customerNumber, childPractices]);

  return (
    <>
      <PageHeader src={sharpsIcon} headerName={'Sharps & Medical Waste'} size={'26px'} />
      <Box sx={{ m: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pb: 2 }}>
          <ExportButton
            category="mwaste"
            childPractices={childPractices}
            customerNumber={customerNumber}
            />
        </Box>
        <div className="flex flex-space-between flex-align-start m-b-16">
          <ComplianceStatus
            complianceCounts={complianceCounts}
            complianceFilter={complianceFilter}
            handleComplianceFilter={handleCheckboxComplianceFilter}
            loading={isLoading}
          />
          <Stats loading={isLoading} statsHeader="Item" statsContent={statsContent} />
        </div>
        <WasteMangementTable
          category="mwaste"
          childPractices={childPractices}
          complianceFilter={complianceFilter}
          groups={groups}
          handleComplianceFilter={handleComplianceFilter}
          loading={isLoading}
        />
      </Box>
    </>
  );
};

export default MedicalWaste;
